import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import dayjs from 'dayjs'
import _ from 'lodash'
import { CircleDashed } from 'tabler-icons-react'
import { useLocation, useParams } from 'react-router-dom'

// Images
import UsersIcon from '../../assets/images/users.svg'

// Components
import { AddUserModal } from '../../components/AddUserModal'
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { Dialog } from '../../components/Dialog'
import { StateContainer } from '../../components/StateContainer'
import { TextInput } from '../../components/TextInput'
import { UserSentEmailsModal } from '../../components/UserSentEmailsModal'

// Store
import { UserStoreContext } from '../../stores/UserStore'

// Service
import { updateUser } from '../../services/users.service'
import {
  addOrganizationUser,
  deleteOrganizationRole,
  getOrganizationUsers,
  resendOrganizationRoleInvitation,
  updateOrganizationRole,
} from '../../services/organizations.service'

// Utils & Styles
import colors from '../../utils/colors'
import { handlePagination, toast } from '../../utils/helpers'

/**
 *
 * OrganizationUsers
 *
 */
const OrganizationUsers = observer(() => {
  // Context
  const { isEEUser, isOrganizationAdmin, user } = useContext(UserStoreContext)
  const location = useLocation()
  const { orgId } = useParams()

  // State
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [users, setUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [loadingUser, setLoadingUser] = useState(false)
  const [editUser, setEditUser] = useState(false)
  const [emailHistoryUser, setEmailHistoryUser] = useState(null)
  const [roleToRemove, setRoleToRemove] = useState(null)
  const [adminCount, setAdminCount] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [pages, setPages] = useState(null)

  // Ref
  const searchInputRef = useRef(null)

  const exhibitorView = location.pathname.includes('exhibitor')
  const canManageUsers = isEEUser || isOrganizationAdmin(orgId)
  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  /**
   * Counts the organization's admins so the last one can be kept out of reach.
   *
   * The list is paginated, so the rows on screen aren't enough to tell -- the only other admin
   * may be three pages away. The API enforces this either way (EXEC-1597); this is so the
   * portal doesn't offer an action that is going to be refused.
   */
  const getAdminCount = async () => {
    // Drop the old figure first. A count left over from before a removal or a demotion says
    // there are still two admins when there is now one, which is exactly the window in which
    // the page would offer to remove the last one.
    setAdminCount(null)

    const response = await getOrganizationUsers(
      `/organizations/${orgId}/roles/?role=Admin&limit=1`,
      handleErrors,
      () => {},
      () => {},
    )

    if (response) setAdminCount(response.count)
  }

  /**
   * Whether `row` may not be removed or demoted for being the organization's last admin.
   *
   * An unknown count -- still loading, or the request failed -- counts as protected. The API
   * refuses either way, so withholding the action briefly beats offering one that bounces.
   * @param {object} row
   */
  const isLastAdmin = (row) => row?.role === 'Admin' && (adminCount === null || adminCount <= 1)

  /**
   * Whether we actually know `row` is the last admin, as opposed to not knowing yet. Only a
   * known one earns an explanation; the loading case just stays disabled.
   * @param {object} row
   */
  const isKnownLastAdmin = (row) => isLastAdmin(row) && adminCount !== null

  /**
   * Gets the updated list of users; updates pagination.
   * @param {string} url
   * @returns list of results
   */
  const getUpdatedUserList = async (url) => {
    const response = await getOrganizationUsers(url, handleErrors, setLoadingUsers, () => {})

    if (response) {
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
      setUsers(response.results)
    }

    getAdminCount()
  }

  /**
   * When the filter or row count changes, get the updated list of users.
   */
  useEffect(() => {
    if (filter) {
      getUpdatedUserList(`/organizations/${orgId}/roles/?expand=user&limit=${perPage}&${filter}`)
    } else {
      getUpdatedUserList(`/organizations/${orgId}/roles/?expand=user&limit=${perPage}`)
    }
  }, [filter, perPage])

  /**
   * Updates the search query based on `search`.
   * @param {string} search
   */
  const updateSearch = (search) => {
    let updatedFilter = ''
    if (search) updatedFilter = `q=${search}`

    setFilter(updatedFilter)
  }

  const filterUsers = useCallback(_.debounce(updateSearch, 500), [])

  const resetForm = () => {
    setEditUser(null)
    setShowUserModal(false)
  }

  /**
   * Handles submitting the add or edit user forms.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    const updatedData = { ...data }
    if (editUser) {
      const updatedUser = { ...updatedData }
      const { roleId: id, role } = updatedUser
      delete updatedUser.phoneNumber
      delete updatedUser.mfaDevice
      delete updatedUser.role

      setLoadingUser(true)
      const savedUser = await updateUser(
        updatedUser,
        handleErrors,
        () => {},
        () => {},
      )

      // Only PATCH the role when it actually changed and this user is allowed to change it.
      // The control is hidden from everyone else, so sending the role regardless would fail
      // the save for someone who only corrected a name.
      const changingRole = canManageUsers && role !== editUser.role
      let savedRole = null

      if (changingRole) {
        savedRole = await updateOrganizationRole(
          { id, organization: orgId, role },
          handleErrors,
          () => {},
          () => {},
        )
      }

      setLoadingUser(false)

      // Both halves have to land before this reports success -- the name saving and the role
      // change being refused is not "updated".
      if (savedUser && (!changingRole || savedRole)) {
        getUpdatedUserList(`/organizations/${orgId}/roles/?expand=user&limit=${perPage}`)
        handleSuccess(`${data.firstName} ${data.lastName} updated.`)
        resetForm()
      }
    } else {
      updatedData.organization = orgId
      updatedData.user = updatedData.email

      delete updatedData.email

      addOrganizationUser(updatedData, handleErrors, setLoadingUser, (m) => {
        getUpdatedUserList(`/organizations/${orgId}/roles/?expand=user&limit=${perPage}`)
        handleSuccess(m)
        resetForm()
      })
    }
  }

  /**
   * Removes the pending role from the organization.
   *
   * Removal is organization-scoped: the account and any role the user holds at another
   * organization are untouched, and any license they were holding is released for reassignment.
   */
  const removeRole = async () => {
    // Take the row and drop it before awaiting. `Dialog` closes itself on confirm but only
    // unmounts when `roleToRemove` clears, so leaving it set for the length of the request
    // means a second Remove click re-uses the same already-closed dialog -- no prompt appears,
    // and the new selection is wiped when the first request lands.
    const role = roleToRemove
    setRoleToRemove(null)

    const removed = await deleteOrganizationRole(
      orgId,
      role.id,
      handleErrors,
      setLoadingUsers,
      handleSuccess,
    )

    if (removed) {
      getUpdatedUserList(`/organizations/${orgId}/roles/?expand=user&limit=${perPage}`)
    }
  }

  /**
   * Renders the `Status` cell in the DataTable
   * - Displays a pill label based on user `status` or `role`
   * @param {object} row
   */
  const renderStatusCell = (row) => {
    if (row.user.lastLogin === null) {
      return (
        <div className="flex shrink-0 flex-row space-x-6">
          <div className="rounded-full bg-status-blue px-2.5 py-0.5">
            <span className="text-xs font-bold text-status-darkBlue">Invite Pending</span>
          </div>

          {isOrganizationAdmin(orgId) && (
            <button
              disabled={loadingResend && loadingResend === row.id}
              type="button"
              className="px-2 py-0.5 font-bold text-purple hover:rounded-full hover:bg-status-purple disabled:opacity-50"
              onClick={async () =>
                resendOrganizationRoleInvitation(
                  orgId,
                  row.id,
                  handleErrors,
                  setLoadingResend,
                  (m) => {
                    getUpdatedUserList(
                      `/organizations/${orgId}/roles/?expand=user&limit=${perPage}`,
                    )
                    handleSuccess(m)
                  },
                )
              }
            >
              <div className="flex flex-row items-center">
                {loadingResend && loadingResend === row.id && (
                  <svg className="mr-1 h-4 w-4 motion-safe:animate-spin-slow" viewBox="0 0 24 24">
                    <CircleDashed size={24} strokeWidth={2} color={colors.purple.DEFAULT} />
                  </svg>
                )}
                Resend
              </div>
            </button>
          )}
        </div>
      )
    }
    return (
      <div className="rounded-full bg-status-green px-2.5 py-0.5">
        <span className="text-xs font-bold text-role-admin">Active</span>
      </div>
    )
  }

  return (
    <>
      <StateContainer>
        <div className="relative flex h-full w-full flex-col space-y-4 overflow-y-auto p-3">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <div className="flex flex-row items-center space-x-1">
                <img src={UsersIcon} alt="Users" className="h-4" />
                <span className="text-md font-bold">Users</span>
              </div>

              <span className="text-xs">Manage user permissions and add users</span>
            </div>

            {/* Creating a role is EE staff and organization admins only
                (`is_ee_user | is_organization_admin`). Offering the button to anyone else
                only led to a 403 once they filled the form in. */}
            {canManageUsers && (
              <Button
                background="bg-purple border-purple hover:bg-purple-600"
                className="absolute right-4"
                icon={<PlusIcon className="h-5 sm:h-6" />}
                label="Add User"
                onClick={() => setShowUserModal(true)}
              />
            )}
          </div>

          <TextInput
            className="w-full rounded-full py-2.5 pl-10 pr-4  placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
            icon={<MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />}
            id="search"
            endIcon={
              searchTerm ? (
                <button
                  type="button"
                  onClick={() => {
                    filterUsers('')
                    setSearchTerm('')
                    searchInputRef.current.value = ''
                  }}
                >
                  <XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />
                </button>
              ) : null
            }
            name="search"
            onChange={(e) => {
              filterUsers(e.target.value)
              setSearchTerm(e.target.value)
            }}
            placeholder="Looking for something?"
            ref={searchInputRef}
            value={searchTerm}
          />

          <div className="grid w-full">
            <DataTable
              columns={[
                {
                  id: 'name',
                  grow: 1,
                  name: 'Name',
                  selector: (row) => `${row.user.firstName} ${row.user.lastName}`,
                  cell: (row) => (
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-dark">
                        {row.user.firstName} {row.user.lastName}
                      </span>
                      {row.user.lastLogin && (
                        <span className="text-xs text-gray-600">
                          Last Logged In: {dayjs(row.user.lastLogin).format('M/D/YYYY h:mm A')}
                        </span>
                      )}
                    </div>
                  ),
                  sortable: true,
                  sortBy: 'user__first_name',
                  minWidth: '250px',
                },
                {
                  id: 'email',
                  grow: 1,
                  name: 'Email',
                  selector: (row) => row.user.email,
                  sortable: true,
                  sortBy: 'user__email',
                  minWidth: '220px',
                },
                {
                  id: 'role',
                  grow: 0.25,
                  name: 'Role',
                  selector: (row) => row.role,
                  sortable: true,
                  sortBy: 'role',
                },
                {
                  id: 'status',
                  grow: 0.75,
                  name: 'Status',
                  cell: (row) => renderStatusCell(row),
                },
                {
                  id: 'edit',
                  grow: isEEUser ? 0.75 : 0.5,
                  name: '',
                  cell: (row) => (
                    <div className="flex flex-row items-center gap-1">
                      {isEEUser && (
                        <button
                          className="font-bold text-purple hover:rounded-full hover:bg-status-purple hover:px-2 hover:py-0.5"
                          type="button"
                          onClick={() => setEmailHistoryUser(row.user)}
                        >
                          Emails
                        </button>
                      )}

                      <button
                        className="font-bold text-blue-600 hover:rounded-full hover:bg-status-blue hover:px-2 hover:py-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={row.user.id === user.id}
                        type="button"
                        onClick={() => {
                          const userToEdit = {
                            // User information
                            id: row.user.id,
                            firstName: row.user.firstName,
                            lastName: row.user.lastName,
                            email: row.user.email,
                            phoneNumber: row.user.phoneNumber,
                            mfaDevice: row.user.mfaDevice,
                            // Organization role information
                            role: row.role,
                            roleId: row.id,
                          }

                          setEditUser(userToEdit)
                          setShowUserModal(true)
                        }}
                      >
                        Edit
                      </button>

                      {canManageUsers && (
                        <button
                          className="font-bold text-error-dark hover:rounded-full hover:bg-error-light hover:px-2 hover:py-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={row.user.id === user.id || isLastAdmin(row)}
                          title={
                            (isKnownLastAdmin(row) &&
                              'This is the only admin for this organization. Make someone else an admin first.') ||
                            (row.user.id === user.id && 'You cannot remove yourself.') ||
                            undefined
                          }
                          type="button"
                          onClick={() => setRoleToRemove(row)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ),
                  center: true,
                },
              ]}
              data={users}
              defaultSortFieldId="name"
              defaultSortAsc
              onChangePage={(page) =>
                handlePagination(
                  page,
                  currentPage,
                  perPage,
                  totalRows,
                  pages,
                  setCurrentPage,
                  getUpdatedUserList,
                  `/organizations/${orgId}/roles/?expand=user&limit=`,
                  filter,
                )
              }
              onChangeRowsPerPage={async (currentRowsPerPage) => setPerPage(currentRowsPerPage)}
              onSort={(column, direction) => {
                const d = direction === 'asc' ? '' : '-'
                const url = `/organizations/${orgId}/roles/?expand=user&order_by=${d}${column.sortBy}&limit=${perPage}`
                getUpdatedUserList(url)
              }}
              pagination
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 15, 20, 30, 50]}
              paginationTotalRows={totalRows}
              paginationServer
              progressPending={loadingUsers}
              sortServer
            />
          </div>
        </div>
      </StateContainer>

      {emailHistoryUser && (
        <UserSentEmailsModal
          setShowModal={() => setEmailHistoryUser(null)}
          showModal={!!emailHistoryUser}
          user={emailHistoryUser}
        />
      )}

      {roleToRemove && (
        <Dialog
          message={`${roleToRemove.user.firstName} ${roleToRemove.user.lastName} will lose access to this organization. Any license they hold is released for reassignment. Their account and any access they have elsewhere are unaffected.`}
          onCancel={() => setRoleToRemove(null)}
          onConfirm={removeRole}
          title="Remove this user?"
          type="warning"
        />
      )}

      {showUserModal && (
        <AddUserModal
          loadingUser={loadingUser}
          onSubmit={onSubmit}
          editUser={editUser}
          endOfFormContent={
            isKnownLastAdmin(editUser)
              ? 'This is the only admin for this organization. Make someone else an admin before changing this role.'
              : null
          }
          roles={{
            // The API allows a role change for EE staff and organization admins only
            // (`is_ee_user | is_organization_admin`), so nobody else is offered the control.
            allowRoleChange: canManageUsers,
            key: 'role',
            options: [
              {
                label: 'Admin',
                description:
                  'Admins can access all Events and manage Qualifiers and Materials. Admins can also add, edit, and delete other Users.',
              },
              {
                label: 'Booth Staff',
                description:
                  'Booth Staff will only have access to Events they are assigned Licenses for Booth Staff can be managed from the Event License page.',
                // Booth Staff are created by assigning a license, never directly -- but an
                // admin added by mistake has to be demotable, and the organization's last
                // admin never is (EXEC-1597).
                disabled: !editUser || isLastAdmin(editUser),
              },
            ],
            showRoleDescriptions: !!exhibitorView,
          }}
          setEditUser={setEditUser}
          showPhoneNumber
          showRoleDescriptions
          showUserModal={showUserModal}
          setShowUserModal={setShowUserModal}
        />
      )}
    </>
  )
})

export default OrganizationUsers
